import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { waypointTypeConfig, WaypointTypeConfig } from 'src/app/ops/config/waypoint-type-config';
import { RuleMatchType, WaypointType } from 'src/graphql/generated/graphql.generated';
import { WaypointEntityFetchService } from 'src/store/generated/client-sync.generated';
import { LocationProps } from 'src/store/generated/location-record.generated';
import {
  WaypointRecord,
  WaypointViewModelBase,
} from 'src/store/generated/waypoint-record.generated';
import { undefinedOrSwitchMap } from 'src/util/rxjs/operators';
import { QueryOptions } from 'src/store/fetch-services';
import { LocationViewModel, LocationViewModelService } from './location-view-model';

export class WaypointViewModel extends WaypointViewModelBase {
  get config(): WaypointTypeConfig {
    return waypointTypeConfig[this.type];
  }

  get locationProps(): Partial<LocationProps> {
    if (this.location != null) {
      return this.location;
    }
    return {
      abbreviation: this.abbreviation,
      address: this.address,
      isRotorWingLz: this.isRotorWingLz,
      name: this.name,
      timezoneCd: this.timezoneCd,
    };
  }

  get locationDisplay() {
    return [this.name, this.abbreviation, this.address.streetAddress, 'Unknown'].find(
      (v) => v != null,
    );
  }

  get displayType() {
    return this.config.name;
  }

  get icon() {
    return this.config.icon;
  }

  get ruleMatchType(): RuleMatchType {
    return ruleMatchType(this.type);
  }
  constructor(waypoint: WaypointRecord, public readonly location: LocationViewModel | undefined) {
    super(waypoint);
  }

  get pickupOrDropoffDescription(): 'Pickup' | 'Dropoff' | undefined {
    return pickupOrDropoffDescription(this.type);
  }
}
export function pickupOrDropoffDescription(
  waypointType: WaypointType,
): 'Pickup' | 'Dropoff' | undefined {
  if ([WaypointType.SceneLocation, WaypointType.SendingFacility].includes(waypointType)) {
    return 'Pickup';
  } else if ([WaypointType.ReceivingFacility].includes(waypointType)) {
    return 'Dropoff';
  }
  return undefined;
}

export function ruleMatchType(waypointType: WaypointType): RuleMatchType {
  if ([WaypointType.SceneLocation, WaypointType.SendingFacility].includes(waypointType)) {
    return RuleMatchType.PickupLocation;
  } else if ([WaypointType.ReceivingFacility].includes(waypointType)) {
    return RuleMatchType.DropoffLocation;
  }
  return RuleMatchType.Unspecified;
}

@Injectable({ providedIn: 'root' })
export class WaypointViewModelService {
  constructor(
    private readonly _waypointEntityFetchService: WaypointEntityFetchService,
    private _locationViewModel: LocationViewModelService,
  ) {}

  public getById(id: string, options?: QueryOptions): Observable<WaypointViewModel | undefined> {
    return this._waypointEntityFetchService.getById(id, options).pipe(
      undefinedOrSwitchMap((waypoint) => {
        const location$ =
          waypoint.locationId != null
            ? this._locationViewModel.getById(waypoint.locationId, options)
            : of(undefined);

        return location$.pipe(map((location) => new WaypointViewModel(waypoint, location)));
      }),
    );
  }
}
